/**
 * POST/PUT scheduling bookable events (classes, facilities, PT sessions).
 *
 * **Thumbnail / images**
 * - With a new image: send real `multipart/form-data` with one binary part named
 *   `thumbnail` (or `facility_image` / `images`) via `FormData.append(name, file, filename)`.
 * - Without: use `application/json` and `thumbnail_url` (HTTPS) or omit / empty — never
 *   put a `File` or serialized file metadata (`path`, `_writeStream`, etc.) in JSON.
 *
 * Multipart metadata is sent as one `data` field (JSON string, same shape as JSON POST)
 * plus file parts — not per-field form keys, so arrays like `windows` stay typed upstream.
 *
 * File field names (API): thumbnail → thumbnail_url; facility_image → facility_image_url;
 * optional alias image → thumbnail_url; repeated images → additional gallery uploads.
 */

export function schedulingPayloadHasFiles(files) {
  if (!files || typeof files !== "object") return false;
  for (const v of Object.values(files)) {
    if (v == null) continue;
    if (Array.isArray(v)) {
      if (v.length > 0) return true;
    } else {
      return true;
    }
  }
  return false;
}

/**
 * Omit `coach_ids` when empty so we never send `[]` or the literal string `"[]"`.
 */
function sanitizeCoachIds(payload) {
  const out = { ...payload };
  const cid = out.coach_ids;
  if (cid === undefined) return out;
  if (Array.isArray(cid) && cid.length === 0) {
    delete out.coach_ids;
    return out;
  }
  if (typeof cid === "string" && cid.trim() === "[]") {
    delete out.coach_ids;
    return out;
  }
  return out;
}

/**
 * Drop anything that must not appear as JSON or as multipart *text* fields: raw
 * Blob/File, or object-shaped `thumbnail` / `image` / `facility_image` (e.g. server
 * file descriptors with `path`, `_writeStream`). URLs use `thumbnail_url` etc.
 */
function sanitizeSchedulingPayload(payload) {
  const out = sanitizeCoachIds({ ...payload });
  for (const key of Object.keys(out)) {
    if (out[key] instanceof Blob) {
      delete out[key];
    }
  }
  for (const slot of ["thumbnail", "image", "facility_image"]) {
    if (slot in out && typeof out[slot] !== "string") {
      delete out[slot];
    }
  }
  return out;
}

/** JSON body: never serialize File/Blob (would stringify badly or omit wrong). */
function stringifySchedulingJson(body) {
  return JSON.stringify(body, (_, value) => {
    if (value instanceof Blob) {
      return undefined;
    }
    return value;
  });
}

/**
 * Remove Blob/File from the text-field payload so they are only sent via the
 * `files` argument (single `thumbnail` / `facility_image` parts, not serialized).
 */
function stripMultipartFileKeysFromPayload(payload) {
  const out = { ...payload };
  for (const k of Object.keys(out)) {
    if (out[k] instanceof Blob) {
      delete out[k];
    }
  }
  return out;
}

/**
 * @param {string} url - /actions/scheduling/...
 * @param {string} method - POST | PUT
 * @param {Record<string, unknown>} payload - same shape as JSON API
 * @param {null | { thumbnail?: Blob, facility_image?: Blob, images?: Blob[] }} files
 */
export async function fetchSchedulingBookable(url, method, payload, files) {
  const bodyPayload = sanitizeSchedulingPayload(payload);
  if (!schedulingPayloadHasFiles(files)) {
    return fetch(url, {
      method,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: stringifySchedulingJson(bodyPayload),
    });
  }

  const fieldPayload = stripMultipartFileKeysFromPayload(bodyPayload);
  const fd = new FormData();
  fd.append("data", stringifySchedulingJson(fieldPayload));

  for (const [key, val] of Object.entries(files)) {
    if (!val) continue;
    if (Array.isArray(val)) {
      for (const item of val) {
        if (item) fd.append(key, item, item.name || `${key}.jpg`);
      }
    } else {
      fd.append(key, val, val.name || `${key}.jpg`);
    }
  }

  return fetch(url, {
    method,
    credentials: "same-origin",
    body: fd,
  });
}

/**
 * Maps facility gallery rows to multipart file fields. The first gallery *slot*
 * maps to facility_image_url (existing URL in JSON) or facility_image (file).
 * Further new files use the images field. Thumbnail is separate.
 * @param {Array<{ file?: Blob, url?: string }>} galleryItems
 * @param {Blob | null | undefined} thumbnailFile
 * @returns {null | { thumbnail?: Blob, facility_image?: Blob, images?: Blob[] }}
 */
export function buildFacilitySchedulingFiles(galleryItems, thumbnailFile) {
  const out = {};
  if (thumbnailFile) out.thumbnail = thumbnailFile;
  const items = galleryItems || [];
  const filesInOrder = items.map((g) => g.file).filter(Boolean);
  if (filesInOrder.length === 0) {
    return schedulingPayloadHasFiles(out) ? out : null;
  }
  if (items[0]?.file) {
    out.facility_image = items[0].file;
    filesInOrder.slice(1).forEach((f, i) => {
      out[`facility_image_${i}`] = f;
    });
  } else {
    filesInOrder.forEach((f, i) => {
      out[`facility_image_${i}`] = f;
    });
  }
  return schedulingPayloadHasFiles(out) ? out : null;
}
